import React, { useEffect, useState } from 'react';
import { X, Edit2, Trash2, Check, XCircle } from 'lucide-react';
import axios from 'axios';
import moment from 'moment';
import { toast } from 'react-toastify';
import Loader from 'components/common/Loader';

const WhatsNewEnhanced = ({ onClose }) => {
  const [whatsNewData, setWhatsNewData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [editingId, setEditingId] = useState(null);
  const [editedDescription, setEditedDescription] = useState('');
  const [deleteConfirmId, setDeleteConfirmId] = useState(null);
  const [actionLoading, setActionLoading] = useState(false);

  const BASE_URL = process.env.REACT_APP_REST_API_ENDPOINT;

  const getPlainText = (value) => {
    if (!value) return '';
    if (typeof window !== 'undefined' && window.DOMParser) {
      const doc = new window.DOMParser().parseFromString(value, 'text/html');
      return (doc.body.textContent || '').trim();
    }
    return String(value)
      .replace(/<[^>]*>/g, '')
      .trim();
  };

  const fetchWhatsNewData = async () => {
    try {
      setLoading(true);
      const response = await axios.get(`${BASE_URL}whatsnew`);

      if (response.data && response.data.message === 'Success') {
        setWhatsNewData(response.data.data);
      } else {
        setError('Failed to fetch data');
      }
    } catch (err) {
      console.error("Error fetching What's New data:", err);
      setError(err.message || 'Something went wrong');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchWhatsNewData();
  }, []);

  const formatDate = (dateString) => {
    return moment(dateString).format('MMMM DD, YYYY');
  };

  const handleEditClick = (item) => {
    setEditingId(item.id);
    setEditedDescription(getPlainText(item.description));
    setDeleteConfirmId(null);
  };

  const handleCancelEdit = () => {
    setEditingId(null);
    setEditedDescription('');
  };

  const handleUpdateClick = async (id) => {
    if (!editedDescription.trim()) {
      toast.error('Description cannot be empty');
      return;
    }

    try {
      setActionLoading(true);
      const response = await axios.put(`${BASE_URL}whatsnew/${id}`, {
        description: getPlainText(editedDescription),
      });

      if (response.data && response.data.message === 'Success') {
        // Update local state
        setWhatsNewData((prevData) =>
          prevData.map((item) =>
            item.id === id
              ? { ...item, description: getPlainText(editedDescription) }
              : item,
          ),
        );
        setEditingId(null);
        setEditedDescription('');
        toast.success('Updated successfully!');
      } else {
        toast.error('Failed to update');
      }
    } catch (err) {
      console.error('Error updating data:', err);
      toast.error(err.response?.data?.message || 'Failed to update');
    } finally {
      setActionLoading(false);
    }
  };

  const handleDeleteClick = (id) => {
    setDeleteConfirmId(id);
    setEditingId(null);
  };

  const handleConfirmDelete = async (id) => {
    try {
      setActionLoading(true);
      const response = await axios.delete(`${BASE_URL}whatsnew/${id}`);

      if (response.data && response.data.message === 'Success') {
        // Remove from local state
        setWhatsNewData((prevData) =>
          prevData.filter((item) => item.id !== id),
        );
        setDeleteConfirmId(null);
        toast.success('Deleted successfully!');
      } else {
        toast.error('Failed to delete');
      }
    } catch (err) {
      console.error('Error deleting data:', err);
      toast.error(err.response?.data?.message || 'Failed to delete');
    } finally {
      setActionLoading(false);
    }
  };

  const handleCancelDelete = () => {
    setDeleteConfirmId(null);
  };

  return (
    <div
      className="notification-static-drawer whatsnew-static-drawer"
      style={{
        position: 'fixed',
        right: 0,
        top: 0,
        width: '30%',
        height: '100vh',
        background: '#fff',
        boxShadow: '-2px 0 10px rgba(0,0,0,0.1)',
        display: 'flex',
        flexDirection: 'column',
        zIndex: 1000,
        fontFamily: "'Work Sans', sans-serif",
      }}
    >
      {/* Header */}
      <div
        style={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          padding: '15px 20px',
          borderBottom: '2px solid #2378C9',
          background: '#f8f9fa',
        }}
      >
        <h2
          style={{
            margin: 0,
            color: 'black',
            fontWeight: 600,
            fontSize: '18px',
          }}
        >
          What&apos;s New
        </h2>
        <button
          type="button"
          onClick={onClose}
          style={{
            background: 'transparent',
            border: 'none',
            cursor: 'pointer',
            padding: '5px',
          }}
        >
          <X size={22} color="#333" />
        </button>
      </div>

      {/* Content */}
      <div
        style={{
          flex: 1,
          overflowY: 'auto',
          padding: '20px',
          background: 'floralwhite',
        }}
      >
        {loading ? (
          <div
            style={{
              display: 'flex',
              justifyContent: 'center',
              padding: '40px',
            }}
          >
            <Loader />
          </div>
        ) : error ? (
          <div
            style={{
              textAlign: 'center',
              padding: '40px',
              color: '#dc3545',
              background: '#f8d7da',
              borderRadius: '8px',
              border: '1px solid #f5c6cb',
            }}
          >
            <p style={{ margin: 0, fontWeight: 'bold' }}>
              ⚠️ Error Loading Data
            </p>
            <p style={{ margin: '8px 0 0 0', fontSize: '14px' }}>{error}</p>
          </div>
        ) : whatsNewData.length === 0 ? (
          <div
            style={{
              textAlign: 'center',
              padding: '40px',
              color: '#6c757d',
              background: '#f8f9fa',
              borderRadius: '8px',
              border: '1px solid #dee2e6',
            }}
          >
            <p style={{ margin: 0, fontSize: '16px' }}>
              📭 No updates available
            </p>
            <p style={{ margin: '8px 0 0 0', fontSize: '14px' }}>
              Check back later for new announcements!
            </p>
          </div>
        ) : (
          whatsNewData.map((item) => (
            <div
              key={item.id}
              style={{ marginBottom: '25px', position: 'relative' }}
            >
              {/* Title & Date & Actions */}
              <div
                style={{
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'flex-start',
                  marginBottom: '10px',
                  gap: '10px',
                }}
              >
                <h5
                  style={{
                    color: '#2378C9',
                    margin: 0,
                    fontSize: '16px',
                    fontWeight: 'bold',
                    flex: 1,
                  }}
                >
                  {item.title}
                </h5>
                <div
                  style={{ display: 'flex', alignItems: 'center', gap: '8px' }}
                >
                  <small
                    style={{
                      color: '#999',
                      fontSize: '12px',
                      whiteSpace: 'nowrap',
                    }}
                  >
                    {formatDate(item.createdAt)}
                  </small>
                  {editingId !== item.id && deleteConfirmId !== item.id && (
                    <>
                      <button
                        type="button"
                        onClick={() => handleEditClick(item)}
                        style={{
                          background: 'transparent',
                          border: 'none',
                          cursor: 'pointer',
                          padding: '4px',
                          display: 'flex',
                          alignItems: 'center',
                        }}
                        title="Edit"
                      >
                        <Edit2 size={16} color="#2378C9" />
                      </button>
                      <button
                        type="button"
                        onClick={() => handleDeleteClick(item.id)}
                        style={{
                          background: 'transparent',
                          border: 'none',
                          cursor: 'pointer',
                          padding: '4px',
                          display: 'flex',
                          alignItems: 'center',
                        }}
                        title="Delete"
                      >
                        <Trash2 size={16} color="#dc3545" />
                      </button>
                    </>
                  )}
                </div>
              </div>

              {/* Image */}
              {item.imageUrl && (
                <div
                  style={{
                    marginBottom: '10px',
                    width: '100%',
                    height: '260px',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    overflow: 'hidden',
                  }}
                >
                  <img
                    src={item.imageUrl}
                    alt={item.title}
                    style={{
                      maxWidth: '100%',
                      maxHeight: '100%',
                      width: 'auto',
                      height: 'auto',
                      display: 'block',
                    }}
                    onError={(e) => {
                      e.target.style.display = 'none';
                    }}
                  />
                </div>
              )}

              {/* Description or Edit Mode */}
              {editingId === item.id ? (
                <div style={{ marginBottom: '10px' }}>
                  <textarea
                    value={editedDescription}
                    onChange={(e) => setEditedDescription(e.target.value)}
                    style={{
                      width: '100%',
                      minHeight: '100px',
                      padding: '10px',
                      fontSize: '14px',
                      border: '2px solid #2378C9',
                      borderRadius: '8px',
                      fontFamily: "'Work Sans', sans-serif",
                      resize: 'vertical',
                    }}
                  />
                  <div
                    style={{
                      display: 'flex',
                      gap: '10px',
                      marginTop: '10px',
                      justifyContent: 'flex-end',
                    }}
                  >
                    <button
                      type="button"
                      onClick={handleCancelEdit}
                      disabled={actionLoading}
                      style={{
                        padding: '8px 16px',
                        background: '#6c757d',
                        color: 'white',
                        border: 'none',
                        borderRadius: '6px',
                        cursor: actionLoading ? 'not-allowed' : 'pointer',
                        fontSize: '14px',
                        fontWeight: 500,
                        display: 'flex',
                        alignItems: 'center',
                        gap: '6px',
                        opacity: actionLoading ? 0.6 : 1,
                      }}
                    >
                      <XCircle size={16} />
                      Cancel
                    </button>
                    <button
                      type="button"
                      onClick={() => handleUpdateClick(item.id)}
                      disabled={actionLoading}
                      style={{
                        padding: '8px 16px',
                        background: '#28a745',
                        color: 'white',
                        border: 'none',
                        borderRadius: '6px',
                        cursor: actionLoading ? 'not-allowed' : 'pointer',
                        fontSize: '14px',
                        fontWeight: 500,
                        display: 'flex',
                        alignItems: 'center',
                        gap: '6px',
                        opacity: actionLoading ? 0.6 : 1,
                      }}
                    >
                      <Check size={16} />
                      {actionLoading ? 'Updating...' : 'Update'}
                    </button>
                  </div>
                </div>
              ) : (
                <div
                  style={{
                    color: '#666',
                    fontSize: '14px',
                    lineHeight: '1.6',
                    wordBreak: 'break-word',
                    whiteSpace: 'pre-wrap',
                  }}
                >
                  {getPlainText(item.description)}
                </div>
              )}

              {/* Delete Confirmation */}
              {deleteConfirmId === item.id && (
                <div
                  style={{
                    background: '#fff3cd',
                    border: '2px solid #ffc107',
                    borderRadius: '8px',
                    padding: '15px',
                    marginTop: '10px',
                  }}
                >
                  <p
                    style={{
                      margin: '0 0 10px 0',
                      color: '#856404',
                      fontWeight: 600,
                      fontSize: '14px',
                    }}
                  >
                    ⚠️ Are you sure you want to delete this item?
                  </p>
                  <div
                    style={{
                      display: 'flex',
                      gap: '10px',
                      justifyContent: 'flex-end',
                    }}
                  >
                    <button
                      type="button"
                      onClick={handleCancelDelete}
                      disabled={actionLoading}
                      style={{
                        padding: '8px 16px',
                        background: '#6c757d',
                        color: 'white',
                        border: 'none',
                        borderRadius: '6px',
                        cursor: actionLoading ? 'not-allowed' : 'pointer',
                        fontSize: '14px',
                        fontWeight: 500,
                        opacity: actionLoading ? 0.6 : 1,
                      }}
                    >
                      Cancel
                    </button>
                    <button
                      type="button"
                      onClick={() => handleConfirmDelete(item.id)}
                      disabled={actionLoading}
                      style={{
                        padding: '8px 16px',
                        background: '#dc3545',
                        color: 'white',
                        border: 'none',
                        borderRadius: '6px',
                        cursor: actionLoading ? 'not-allowed' : 'pointer',
                        fontSize: '14px',
                        fontWeight: 500,
                        display: 'flex',
                        alignItems: 'center',
                        gap: '6px',
                        opacity: actionLoading ? 0.6 : 1,
                      }}
                    >
                      <Trash2 size={16} />
                      {actionLoading ? 'Deleting...' : 'Delete'}
                    </button>
                  </div>
                </div>
              )}

              {/* Divider */}
              <hr
                style={{
                  border: 'none',
                  borderTop: '1px solid #e0e0e0',
                  margin: '20px 0 0 0',
                }}
              />
            </div>
          ))
        )}
      </div>
    </div>
  );
};

export default WhatsNewEnhanced;
